<template>
  <div class="mqtt-route">
    <div class="container-fluid py-4">
      <h2 class="h4 fw-medium mb-4">{{ $t("__mqttRouteManagement") }}</h2>
      <div
        class="
          d-flex
          align-items-center
          justify-content-between
          mb-3
          flex-wrap
          gap-2
        "
      >
        <div class="d-flex align-items-center flex-wrap gap-2">
          <form @submit.prevent="searchRoute">
            <div class="input-group rounded-pill">
              <input
                v-model="search"
                type="text"
                class="form-control rounded-pill"
                :placeholder="$t('__searchMqttRoute')"
                aria-label="search"
                aria-describedby="search"
              />
              <button
                type="submit"
                class="btn btn-link px-2 text-dark"
                id="search"
              >
                <i class="bi bi-search"></i>
              </button>
              <button
                v-if="filter || searchAgent"
                type="button"
                class="btn btn-primary px-3 ms-1 rounded-pill"
                id="clearSearch"
                @click="clearSearch"
              >
                {{ $t("__clearSearch") }}
              </button>
            </div>
          </form>
          <div v-if="hasPermission('mqtt-route.delete')">
            <v-select
              v-model="searchAgent"
              :options="managerOptions"
              :reduce="option => option.value"
              :placeholder="$t('__selectManager')"
              :clearable="true"
              label="label"
              class="vs-rounded-pill"
            ></v-select>
          </div>
        </div>
        <div>
          <button
            v-if="hasPermission('mqtt-route.refresh')"
            @click="refreshRouteTable"
            class="ms-2 btn btn-primary rounded-pill"
          >
            <i class="bi bi-arrow-clockwise"></i>
            {{ $t("__refreshRouteTable") }}
          </button>
          <button
            v-if="hasPermission('mqtt-route.create')"
            @click="showModal()"
            class="ms-3 fs-5 btn btn-primary px-2 rounded-circle lh-sm"
          >
            <i class="bi bi-plus"></i>
          </button>
        </div>
      </div>
      <div class="card">
        <div class="card-body">
          <b-table
            :responsive="$store.state.windowWidth > $store.state.breakpoints.sm"
            :stacked="$store.state.windowWidth <= $store.state.breakpoints.sm"
            striped
            borderless
            no-provider-filtering
            head-variant="light"
            :items="items"
            :fields="fields"
            :per-page="0"
            :filter="filter"
            :filter-included-fields="filterOn"
            @filtered="onFiltered"
            :tbody-tr-class="rowClass"
            :sort-compare="sortComputed"
          >
            <template v-slot:head()="scope">
              <div class="text-nowrap">
                {{ scope.label }}
              </div>
            </template>
            <template v-slot:cell(type)="data">
              <span class="badge" :class="{
                'bg-primary': data.item.type === 'agency',
                'bg-success': data.item.type === 'manager'
              }">
                {{ data.item.type === 'agency' ? $t('__agency') : $t('__manager') }}
              </span>
            </template>
            <template v-slot:cell(target)="data">
              <div>
                <strong v-if="data.item.type === 'agency' && data.item.agency">
                  {{ data.item.agency.name }}
                </strong>
                <strong v-else-if="data.item.type === 'manager' && data.item.manager">
                  {{ data.item.manager.name }}
                </strong>
                <div class="text-muted small">
                  {{ $t('__affectedAgencies') }}: {{ data.item.affected_agencies_count }}
                </div>
              </div>
            </template>
            <template v-slot:cell(status)="data">
              <div class="status-switch" v-if="hasPermission('mqtt-route.update')">
                <input
                  type="checkbox"
                  :id="'switch-' + data.index"
                  v-model="data.item.status"
                  @click="changeStatusAlert(data.item.id)"
                />
                <label :for="'switch-' + data.index"></label>
              </div>
            </template>
            <template v-slot:cell(active)="data">
              <div class="d-flex">
                <button
                  v-if="hasPermission('mqtt-route.show')"
                  @click="showPreview(data.item)"
                  type="button"
                  class="btn btn-link px-1 text-info"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__preview')"
                >
                  <i class="bi bi-eye mx-1 fs-5"></i>
                </button>
                <button
                  v-if="hasPermission('mqtt-route.update')"
                  @click="showModal(data.item.id)"
                  type="button"
                  class="btn btn-link px-1 text-dark"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__edit')"
                >
                  <i class="bi bi-pencil mx-1 fs-5"></i>
                </button>
                <button
                  v-if="hasPermission('mqtt-route.delete')"
                  @click="deleteAlert(data.item.id)"
                  type="button"
                  class="btn btn-link px-1 text-danger"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__delete')"
                >
                  <i class="bi bi-trash mx-1 fs-5"></i>
                </button>
              </div>
            </template>
          </b-table>
        </div>
      </div>
      <div class="row align-items-center mt-3" v-if="totalRows">
        <div class="col-sm col-12 text-right text-sm-left">
          {{ $t('__paginationInfo', {
            startNub: currentPage == 1 ? 1 : perPage * (currentPage - 1) + 1,
            entNub: currentPage == 1
              ? totalRows > perPage
                ? perPage
                : totalRows
              : perPage * (currentPage - 1) + perPage > totalRows
              ? totalRows
              : perPage * (currentPage - 1) + perPage ,
            sum: totalRows
            })
          }}
        </div>
        <div class="col-sm col-12">
          <b-pagination
            v-if="totalRows > perPage"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            align="right"
            first-number
            last-number
            class="mb-sm-0 mb-3 mt-sm-0 mt-3"
          ></b-pagination>
        </div>
      </div>
    </div>

    <!-- 新增/編輯 Modal -->
    <div
      class="modal fade cms-style"
      id="mqttRouteModal"
      tabindex="-1"
      ref="mqttRouteModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <form @submit.prevent="saveRoute">
            <div class="modal-header">
              <h5 class="modal-title fw-medium">
                {{ modalTitle }}
              </h5>
              <button
                type="button"
                class="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div class="modal-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label
                      for="type"
                      class="form-label fw-medium required"
                    >{{ $t("__routeType") }}</label>
                    <div class="btn-group w-100" role="group">
                      <input
                        type="radio"
                        class="btn-check"
                        name="type"
                        id="type-agency"
                        value="agency"
                        v-model="modalData.type"
                        :disabled="modalData.id"
                      >
                      <label class="btn btn-outline-primary" for="type-agency">
                        {{ $t("__agency") }}
                      </label>

                      <input
                        type="radio"
                        class="btn-check"
                        name="type"
                        id="type-manager"
                        value="manager"
                        v-model="modalData.type"
                        :disabled="modalData.id"
                      >
                      <label class="btn btn-outline-primary" for="type-manager">
                        {{ $t("__manager") }}
                      </label>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label
                      for="target"
                      class="form-label fw-medium required"
                    >
                      {{ modalData.type === 'agency' ? $t("__selectAgency") : $t("__selectManager") }}
                    </label>
                    <select
                      v-if="modalData.type === 'agency'"
                      v-model="modalData.agency_id"
                      class="form-select"
                      :class="{
                        'is-invalid': validate && validate.msg.agency_id
                      }"
                      required
                    >
                      <option value="">{{ $t("__pleaseSelect") }}</option>
                      <option
                        v-for="agency in agencies"
                        :key="agency.id"
                        :value="agency.id"
                      >
                        {{ agency.name }}
                      </option>
                    </select>
                    <select
                      v-else
                      v-model="modalData.manager_id"
                      class="form-select"
                      :class="{
                        'is-invalid': validate && validate.msg.manager_id
                      }"
                      required
                    >
                      <option value="">{{ $t("__pleaseSelect") }}</option>
                      <option
                        v-for="manager in managers"
                        :key="manager.id"
                        :value="manager.id"
                      >
                        {{ manager.name }}
                      </option>
                    </select>
                    <div class="invalid-feedback" v-if="validate && (validate.msg.agency_id || validate.msg.manager_id)">
                      <div v-for="(msg, index) in (validate.msg.agency_id || validate.msg.manager_id)" :key="index">
                        {{ msg }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label
                      for="prefix"
                      class="form-label fw-medium required"
                    >{{ $t("__topicPrefix") }}</label>
                    <input
                      v-model="modalData.prefix"
                      type="text"
                      class="form-control"
                      :class="{
                        'is-invalid': validate && validate.msg.prefix
                      }"
                      id="prefix"
                      placeholder="例如: agency/001"
                      required
                    />
                    <div class="invalid-feedback" v-if="validate && validate.msg.prefix">
                      <div v-for="(msg, index) in validate.msg.prefix" :key="index">
                        {{ msg }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label
                      for="modal-status"
                      class="form-label fw-medium"
                    >{{ $t("__status") }}</label>
                    <div class="status-switch">
                      <input
                        v-model="modalData.status"
                        type="checkbox"
                        id="modal-status"
                      >
                      <label for="modal-status"></label>
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="mb-3">
                    <label
                      for="description"
                      class="form-label fw-medium"
                    >{{ $t("__description") }}</label>
                    <textarea
                      v-model="modalData.description"
                      class="form-control"
                      id="description"
                      rows="3"
                    ></textarea>
                  </div>
                </div>
              </div>

              <!-- 預覽影響範圍 -->
              <div v-if="previewData" class="alert alert-info">
                <h6 class="alert-heading">{{ $t("__affectedScope") }}</h6>
                <div class="row">
                  <div class="col-md-4">
                    <strong>{{ $t("__agencies") }}:</strong> {{ previewData.summary.agency_count }}
                  </div>
                  <div class="col-md-4">
                    <strong>{{ $t("__devices") }}:</strong> {{ previewData.summary.device_count }}
                  </div>
                  <div class="col-md-4">
                    <strong>{{ $t("__residents") }}:</strong> {{ previewData.summary.resident_count }}
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer justify-content-between">
              <button
                type="button"
                class="btn btn-outline-secondary rounded-pill"
                data-bs-dismiss="modal"
              >
                {{ $t("__cancel") }}
              </button>
              <button type="submit" class="btn btn-primary rounded-pill">
                {{ $t("__save") }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- 預覽 Modal -->
    <div
      class="modal fade cms-style"
      id="previewModal"
      tabindex="-1"
      ref="previewModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title fw-medium">{{ $t("__routeDetail") }}</h5>
            <button
              type="button"
              class="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body" v-if="previewRoute">
            <div class="row mb-3">
              <div class="col-md-6">
                <strong>{{ $t("__routeType") }}:</strong>
                <span class="badge ms-2" :class="{
                  'bg-primary': previewRoute.type === 'agency',
                  'bg-success': previewRoute.type === 'manager'
                }">
                  {{ previewRoute.type === 'agency' ? $t('__agency') : $t('__manager') }}
                </span>
              </div>
              <div class="col-md-6">
                <strong>{{ $t("__status") }}:</strong>
                <span class="badge ms-2" :class="{
                  'bg-success': previewRoute.status,
                  'bg-secondary': !previewRoute.status
                }">
                  {{ previewRoute.status ? $t("__enabled") : $t("__disabled") }}
                </span>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-6">
                <strong>{{ $t("__topicPrefix") }}:</strong> {{ previewRoute.prefix }}
              </div>
              <div class="col-md-6">
                <strong>{{ $t("__target") }}:</strong>
                <span v-if="previewRoute.type === 'agency' && previewRoute.agency">
                  {{ previewRoute.agency.name }}
                </span>
                <span v-else-if="previewRoute.type === 'manager' && previewRoute.manager">
                  {{ previewRoute.manager.name }}
                </span>
              </div>
            </div>
            <div v-if="previewRoute.description" class="mb-3">
              <strong>{{ $t("__description") }}:</strong>
              <p class="mt-2">{{ previewRoute.description }}</p>
            </div>
            <div v-if="previewRoute.affected_agencies" class="mb-3">
              <strong>{{ $t("__affectedAgencies") }} ({{ previewRoute.affected_agencies.length }}):</strong>
              <ul class="mt-2">
                <li v-for="agency in previewRoute.affected_agencies" :key="agency.id">
                  {{ agency.name }} ({{ agency.codename }})
                </li>
              </ul>
            </div>
          </div>
          <div class="modal-footer justify-content-center">
            <button type="button" class="btn btn-outline-secondary rounded-pill" data-bs-dismiss="modal">{{ $t("__close") }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 狀態變更 Modal -->
    <div
      class="modal fade"
      id="changeStatusModal"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabindex="-1"
      ref="changeStatusModal"
    >
      <div class="modal-dialog modal-sm">
        <div class="modal-content">
          <div class="modal-header justify-content-center">
            <h5 class="modal-title">{{ $t("__changeStatus") }}</h5>
          </div>
          <div class="modal-body text-center">
            <p class="mb-0">
              {{ changeStatusItem ?
                (changeStatusItem.status ? $t('__disableRoute') : $t('__enableRoute')) :
                ''
              }}
            </p>
          </div>
          <div class="modal-footer justify-content-between">
            <button
              type="button"
              class="btn btn-outline-secondary rounded-pill"
              data-bs-dismiss="modal"
              @click="cancelStatusChange"
            >
              {{ $t("__cancel") }}
            </button>
            <button
              @click="confirmStatusChange"
              type="button"
              class="btn btn-primary rounded-pill"
            >
              {{ $t("__confirm") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 刪除確認 Modal -->
    <div
      class="modal fade"
      id="deleteModal"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabindex="-1"
      ref="deleteModal"
    >
      <div class="modal-dialog modal-sm">
        <div class="modal-content">
          <div class="modal-header justify-content-center">
            <h5 class="modal-title">{{ $t("__confirmDelete") }}</h5>
          </div>
          <div class="modal-body text-center">
            <p class="mb-0">{{ $t("__deleteWarning") }}</p>
          </div>
          <div class="modal-footer justify-content-between">
            <button
              type="button"
              class="btn btn-outline-secondary rounded-pill"
              data-bs-dismiss="modal"
            >
              {{ $t("__cancel") }}
            </button>
            <button
              @click="confirmDelete"
              type="button"
              class="btn btn-danger rounded-pill"
            >
              {{ $t("__delete") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap'
import i18n from '@/lang/lang.js'
import { mqttRoute } from '@/http/api/mqttRoute.js'
import { agent } from '@/http/api/agent.js'
import { mapState, mapMutations } from 'vuex'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  name: 'MqttRoute',
  components: {
    vSelect
  },
  data () {
    return {
      allManagers: [],
      searchAgent: null,
      items: [],
      fields: [
        {
          key: 'type',
          label: this.$t('__routeType'),
          class: 'align-middle',
          sortable: true
        },
        {
          key: 'prefix',
          label: this.$t('__topicPrefix'),
          class: 'align-middle',
          sortable: true
        },
        {
          key: 'target',
          label: this.$t('__target'),
          class: 'align-middle',
          sortable: false
        },
        {
          key: 'description',
          label: this.$t('__description'),
          class: 'align-middle',
          sortable: false
        },
        {
          key: 'status',
          label: this.$t('__status'),
          class: 'align-middle text-center',
          sortable: true
        },
        {
          key: 'active',
          class: 'align-middle',
          label: i18n.t('__active'),
        }
      ],
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      filter: null,
      filterOn: [],
      search: '',
      modalData: {
        id: null,
        type: 'agency',
        agency_id: '',
        manager_id: '',
        prefix: '',
        description: '',
        status: true
      },
      validate: null,
      modalTitle: '',
      agencies: [],
      managers: [],
      previewData: null,
      previewRoute: null,
      changeStatusId: null,
      changeStatusItem: null,
      deleteId: null,
      statusChangeConfirmed: false
    }
  },
  computed: {
    ...mapState(['userInfo', 'token']),
    permissions () {
      return this.userInfo.permissions
    },
    managerOptions () {
      if (!this.allManagers || !this.allManagers.length) return []
      return this.allManagers.map(item => ({
        value: item.id,
        label: item.name
      }))
    }
  },
  watch: {
    currentPage () {
      this.getMqttRoutes()
    },
    searchAgent () {
      this.currentPage = 1
      this.getMqttRoutes()
    },
    'modalData.type': function (newVal) {
      // 切換類型時清空選擇
      if (newVal === 'agency') {
        this.modalData.manager_id = ''
      } else {
        this.modalData.agency_id = ''
      }
      this.loadPreview()
    },
    'modalData.agency_id': function () {
      this.loadPreview()
    },
    'modalData.manager_id': function () {
      this.loadPreview()
    }
  },
  created () {
    this.getMqttRoutes()
    this.getAgencies()
    this.getManagers()
    this.getAllManagers()
  },
  methods: {
    ...mapMutations(['Loading', 'Loaded', 'AlertsInfo', 'Alerted']),
    hasPermission (permission) {
      if (!this.permissions) return false
      return this.permissions.some(p => p.name === permission)
    },
    getAllManagers () {
      const vm = this
      agent.get('', vm.token).then(res => {
        if (res.status <= 201 && res.data.status === 'success') {
          vm.allManagers = res.data.data
        }
      })
    },
    getMqttRoutes () {
      const vm = this
      vm.Loading()
      const params = {
        page: this.currentPage,
        per_page: this.perPage
      }
      if (this.filter) {
        params.search = this.filter
      }
      if (this.searchAgent) {
        params.manager_id = this.searchAgent
      }
      mqttRoute.get(params, vm.token).then(res => {
        vm.Loaded()  // 確保先關閉 Loading
        if (res.status <= 201) {
          vm.items = res.data.data
          vm.totalRows = res.data.total
          vm.currentPage = res.data.current_page
          vm.perPage = res.data.per_page
        } else {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: res.data.message || i18n.t('__error')
          })
        }
      }).catch(error => {
        vm.Loaded()
        vm.AlertsInfo({
          state: 'error',
          title: i18n.t('__error'),
          info: error.response?.data?.message || error.message
        })
      })
    },
    getAgencies () {
      const vm = this
      mqttRoute.getAgencies(vm.token).then(res => {
        if (res.status <= 201) {
          vm.agencies = res.data
        }
      })
    },
    getManagers () {
      const vm = this
      mqttRoute.getManagers(vm.token).then(res => {
        if (res.status <= 201) {
          vm.managers = res.data
        }
      })
    },
    searchRoute () {
      this.filter = this.search
      this.currentPage = 1
      this.getMqttRoutes()
    },
    clearSearch () {
      this.search = ''
      this.filter = null
      this.searchAgent = null
      this.currentPage = 1
      this.getMqttRoutes()
    },
    showModal (id = null) {
      const vm = this
      vm.validate = null
      vm.previewData = null

      if (id) {
        vm.modalTitle = vm.$t('__editMqttRoute')
        // 移除這裡的 Loading，避免與 Modal 衝突
        mqttRoute.getOne(id, vm.token).then(res => {
          if (res.status <= 201) {
            const data = res.data
            vm.modalData = {
              id: data.id,
              type: data.type,
              agency_id: data.agency_id || '',
              manager_id: data.manager_id || '',
              prefix: data.prefix,
              description: data.description || '',
              status: data.status
            }
            const modal = new Modal(vm.$refs.mqttRouteModal)
            modal.show()
          }
        }).catch(error => {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: error.response?.data?.message || error.message
          })
        })
      } else {
        vm.modalTitle = vm.$t('__addMqttRoute')
        vm.modalData = {
          id: null,
          type: 'agency',
          agency_id: '',
          manager_id: '',
          prefix: '',
          description: '',
          status: true
        }
        const modal = new Modal(vm.$refs.mqttRouteModal)
        modal.show()
      }
    },
    saveRoute () {
      const vm = this
      vm.validate = null
      vm.Loading()

      const data = {
        type: vm.modalData.type,
        prefix: vm.modalData.prefix,
        description: vm.modalData.description,
        status: vm.modalData.status
      }

      if (vm.modalData.type === 'agency') {
        data.agency_id = vm.modalData.agency_id
      } else {
        data.manager_id = vm.modalData.manager_id
      }

      const promise = vm.modalData.id
        ? mqttRoute.update(vm.modalData.id, data, vm.token)
        : mqttRoute.create(data, vm.token)

      promise.then(res => {
        if (res.status <= 201 && res.data.success) {
          vm.AlertsInfo({
            state: 'success',
            title: i18n.t('__success'),
            info: vm.modalData.id ? i18n.t('__editSuccess') : i18n.t('__createSuccess')
          })
          Modal.getInstance(vm.$refs.mqttRouteModal).hide()
          vm.getMqttRoutes()
        } else if (res.status === 422) {
          vm.validate = res.data
        } else {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: res.data.message || i18n.t('__error')
          })
        }
        vm.Loaded()
      }).catch(error => {
        vm.Loaded()
        if (error.response?.status === 422) {
          vm.validate = error.response.data
        } else {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: error.response?.data?.message || error.message
          })
        }
      })
    },
    changeStatusAlert (id) {
      const vm = this
      vm.changeStatusId = id
      vm.changeStatusItem = vm.items.find(i => i.id === id)
      const modal = new Modal(vm.$refs.changeStatusModal)
      modal.show()
      vm.$refs.changeStatusModal.addEventListener('hidden.bs.modal', () => {
        // Modal 關閉時恢復原狀
        if (vm.changeStatusItem && !vm.statusChangeConfirmed) {
          vm.changeStatusItem.status = !vm.changeStatusItem.status
        }
        vm.statusChangeConfirmed = false
      }, { once: true })
    },
    confirmStatusChange () {
      const vm = this
      vm.statusChangeConfirmed = true
      const newStatus = !vm.changeStatusItem.status
      vm.changeStatus(vm.changeStatusId, newStatus)
      Modal.getInstance(vm.$refs.changeStatusModal).hide()
    },
    cancelStatusChange () {
      // 恢復原狀
      if (this.changeStatusItem) {
        this.changeStatusItem.status = !this.changeStatusItem.status
      }
    },
    changeStatus (id, status) {
      const vm = this
      vm.Loading()
      mqttRoute.update(id, { status }, vm.token).then(res => {
        if (res.status <= 201 && res.data.success) {
          vm.AlertsInfo({
            state: 'success',
            title: i18n.t('__success'),
            info: i18n.t('__updateSuccess')
          })
          vm.getMqttRoutes()
        } else {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: res.data.message || i18n.t('__error')
          })
          // 錯誤時不需要重新載入，只需恢復原狀
          item.status = !item.status
        }
        vm.Loaded()
      }).catch(error => {
        vm.Loaded()
        vm.AlertsInfo({
          state: 'error',
          title: i18n.t('__error'),
          info: error.response?.data?.message || error.message
        })
        // 錯誤時不需要重新載入，只需恢復原狀
        item.status = !item.status
      })
    },
    deleteAlert (id) {
      const vm = this
      vm.deleteId = id
      const modal = new Modal(vm.$refs.deleteModal)
      modal.show()
    },
    confirmDelete () {
      const vm = this
      vm.deleteRoute(vm.deleteId)
      Modal.getInstance(vm.$refs.deleteModal).hide()
    },
    deleteRoute (id) {
      const vm = this
      vm.Loading()
      mqttRoute.delete(id, vm.token).then(res => {
        if (res.status <= 201 && res.data.success) {
          vm.AlertsInfo({
            state: 'success',
            title: i18n.t('__success'),
            info: i18n.t('__deleteSuccess')
          })
          vm.getMqttRoutes()
        } else {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: res.data.message || i18n.t('__error')
          })
        }
        vm.Loaded()
      }).catch(error => {
        vm.Loaded()
        vm.AlertsInfo({
          state: 'error',
          title: i18n.t('__error'),
          info: error.response?.data?.message || error.message
        })
      })
    },
    refreshRouteTable () {
      const vm = this
      // 使用 confirm 確認
      const confirmMessage = vm.$t('__confirmRefreshRouteTable') || '確定要立即更新路由表嗎？\n這將會重新載入所有 MQTT 路由設定。'
      if (!confirm(confirmMessage)) {
        return
      }

      vm.Loading()

      mqttRoute.refresh(vm.token).then(res => {
        vm.Loaded()
        if (res.status <= 201) {
          // 根據後端回傳判斷成功或失敗
          if (res.data && (res.data.success || res.data.status === 'success')) {
            vm.AlertsInfo({
              state: 'success',
              title: vm.$t('__success'),
              info: res.data.message || vm.$t('__refreshRouteTableSuccess') || '路由表更新成功'
            })
            vm.Alerted()
            // 重新載入列表
            vm.getMqttRoutes()
          } else {
            // 後端回傳失敗
            vm.AlertsInfo({
              state: 'error',
              title: vm.$t('__error'),
              info: res.data.message || vm.$t('__refreshRouteTableFailed') || '路由表更新失敗'
            })
            vm.Alerted()
          }
        } else {
          // HTTP 狀態碼錯誤
          vm.AlertsInfo({
            state: 'error',
            title: vm.$t('__error'),
            info: `更新失敗 (${res.status}): ${res.data?.message || res.statusText}`
          })
          vm.Alerted()
        }
      }).catch(error => {
        vm.Loaded()
        console.error('Refresh route table error:', error)
        // 網路或其他錯誤
        const errorMessage = error.response?.data?.message ||
                           error.message ||
                           '網路錯誤，請稍後再試'
        vm.AlertsInfo({
          state: 'error',
          title: vm.$t('__error'),
          info: errorMessage
        })
        vm.Alerted()
      })
    },
    loadPreview () {
      const vm = this
      if (!vm.modalData.type) return

      if (vm.modalData.type === 'agency' && !vm.modalData.agency_id) return
      if (vm.modalData.type === 'manager' && !vm.modalData.manager_id) return

      const data = {
        type: vm.modalData.type
      }

      if (vm.modalData.type === 'agency') {
        data.agency_id = vm.modalData.agency_id
      } else {
        data.manager_id = vm.modalData.manager_id
      }

      mqttRoute.preview(data, vm.token).then(res => {
        if (res.status <= 201) {
          vm.previewData = res.data
        }
      }).catch(() => {
        vm.previewData = null
      })
    },
    showPreview (item) {
      const vm = this
      // 移除這裡的 Loading，避免與 Modal 衝突
      mqttRoute.getOne(item.id, vm.token).then(res => {
        if (res.status <= 201) {
          vm.previewRoute = res.data
          const modal = new Modal(vm.$refs.previewModal)
          modal.show()
        }
      }).catch(error => {
        vm.AlertsInfo({
          state: 'error',
          title: i18n.t('__error'),
          info: error.response?.data?.message || error.message
        })
      })
    },
    onFiltered (filteredItems) {
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },
    rowClass (item, type) {
      if (!item || type !== 'row') return
      if (!item.status) return 'table-secondary'
    },
    sortComputed (a, b, key) {
      if (key === 'status') {
        return a.status === b.status ? 0 : a.status ? 1 : -1
      }
      return false
    }
  }
}
</script>

<style lang="scss">
  .vs-rounded-pill {
    min-width: 220px;
    .vs__dropdown-toggle {
      border: 1px solid #ced4da;
      border-radius: 50rem;
      padding: 0.25rem 0.75rem;
      background: #fff;
    }
    .vs__search::placeholder {
      color: #6c757d;
    }
    .vs__clear {
      margin-right: 4px;
    }
  }
</style>
